import { LoyaltyService } from "./services/loyalty/loyalty";
import type {
  TrackEventPayload,
  TrackEventResponse,
} from "./services/loyalty/types";
import type { SDKActionRegistryPayload } from "./state/sdk-action-registry";
import { SDKState } from "./state/sdk-state";
import { SDKOptions } from "./state/sdk-options";
import { SDKTracker } from "./utils/sdk-tracker";
import { ToastNotificationManager } from "./ui/toast-notification-manager";
import { showLoyaltyToastNotification } from "./services/loyalty/show-loyalty-toast-notification";
import {
  EMBED_CONTAINER_ID,
  EMBED_ELEMENT_ID,
  EmbedService,
} from "./services/embed/embed";
import { AuthService } from "./services/auth/auth";
import { ApiService } from "./services/api/api";
import { Logger, LogLevel } from "./utils/logger";
import { WebAppService } from "./services/api/web-app";

export interface SDKUserInfo {
  username: string;
  avatar?: string;
  email: string;
}

export class SDK {
  #logger: Logger;
  #state: SDKState;
  #embedService: EmbedService;
  #loyaltyService: LoyaltyService;
  #tracker: SDKTracker;
  #toastNotificationManager: ToastNotificationManager;
  #authService: AuthService;
  #webAppService: WebAppService;
  #trackingEnabled = false;

  static embedElementId = EMBED_ELEMENT_ID;
  static embedContainerId = EMBED_CONTAINER_ID;

  constructor(
    logger: Logger = new Logger("[FanfestSDK]", {
      logLevel: LogLevel.DEBUG,
    }),
    state: SDKState = new SDKState(logger),
    embedService: EmbedService = new EmbedService(state, logger),
    loyaltyService: LoyaltyService = new LoyaltyService(state, logger),
    tracker: SDKTracker = new SDKTracker(logger),
    toastNotificationManager: ToastNotificationManager = new ToastNotificationManager(
      logger,
      state,
    ),
    apiService: ApiService = new ApiService(state, logger),
    authService: AuthService = new AuthService(
      state,
      apiService,
      logger,
      toastNotificationManager,
    ),
    webAppService: WebAppService = new WebAppService(apiService, logger),
  ) {
    this.#logger = logger;
    this.#state = state;
    this.#embedService = embedService;
    this.#loyaltyService = loyaltyService;
    this.#tracker = tracker;
    this.#toastNotificationManager = toastNotificationManager;
    this.#authService = authService;
    this.#webAppService = webAppService;
  }

  init = async (options: unknown): Promise<void> => {
    if (this.#state.isInitialized) {
      this.#logger.warn("Already initialized");
      return;
    }

    this.#logger.info("Initializing SDK", { options });

    const sdkOptions = new SDKOptions(options, this.#logger);

    this.#state.init(sdkOptions);
    this.#embedService.init();
    this.#authService.init();

    // Initialize tracking unless explicitly disabled by the host site
    // (e.g. waiting for cookie consent). Host calls enableTracking() later.
    if (!sdkOptions.trackingDisabledByDefault) {
      this.#initTracking();
    } else {
      this.#logger.info(
        "Tracking disabled by default — call enableTracking() after user consent",
      );
    }

    this.#initAuthStateChangeCallback();

    this.#logger.debug("SDK initialized with options", {
      sdkOptions,
      state: this.#state,
    });

    if (this.#state.channelInfo.channelId === null) {
      try {
        const webApp = await this.#webAppService.getWebApp();

        this.#logger.info("App info retrieved", { webApp });
        this.#state.channelInfo.value.set(webApp);
      } catch (error) {
        this.#logger.error("Failed to get web app", { error });
      }
    }

    this.#logger.info("SDK initialized");
  };

  #initTracking(): void {
    if (this.#trackingEnabled) return;
    this.#trackingEnabled = true;

    if (this.#state.options.enableToastNotifications) {
      this.#toastNotificationManager.init();
    } else {
      this.#logger.info(
        "Toast notifications disabled by host — skipping ToastNotificationManager.init()",
      );
    }
    this.#tracker.init();
    this.#tracker.onTrackEvent.subscribe((event) =>
      this.trackEvent(event.payload),
    );

    this.#logger.info("Tracking enabled");
  }

  /**
   * Enable tracking (toasts + action tracking). Call this after the user
   * accepts cookies/consent on the host site. One-way: once enabled for
   * a session it cannot be disabled — a page refresh resets the state.
   */
  enableTracking = (): void => {
    if (this.#trackingEnabled) {
      this.#logger.error(
        "Tracking is already enabled for this session. " +
          "Once enabled, tracking cannot be disabled — refresh the page to reset.",
      );
      return;
    }

    if (!this.#state.isInitialized) {
      this.#logger.error(
        "SDK not yet initialized — call init() before enableTracking()",
      );
      return;
    }

    this.#initTracking();
  };

  registerActionMapping = (payload: SDKActionRegistryPayload): void => {
    this.#logger.debug("Registering action mapping", { payload });
    this.#state.actionRegistry.registerActionMapping(payload);
  };

  trackEvent = async (
    payload: TrackEventPayload,
  ): Promise<TrackEventResponse> => {
    if (!this.#trackingEnabled) {
      return { success: false, didReward: false, points: 0 };
    }

    const response = await this.#loyaltyService.trackEvent(payload);

    if (
      response.success &&
      (!this.#state.authState.hasUser || response.didReward)
    ) {
      showLoyaltyToastNotification(
        this.#toastNotificationManager,
        response,
        this.#state,
        this.#authService,
      );
    }

    return response;
  };

  getUser = (): SDKUserInfo | null => {
    const user = this.#state.authState.user;
    if (!user) return null;

    return {
      username: user.username,
      email: user.email,
    };
  };

  login = async () => {
    this.#logger.info("Logging in");
    this.#authService.initiateAuthentication();
  };

  logout = async () => {
    this.#logger.info("Logging out");
    this.#state.authState.value.remove();
  };

  #initAuthStateChangeCallback() {
    const callback = this.#state.options.onAuthStateChange;
    if (!callback) return;

    // Fire immediately with current state
    callback(this.#state.authState.isAuthenticated);

    // Subscribe to changes
    this.#state.authState.value.subject.subscribe(
      (authObject) => {
        callback(authObject !== null);
      },
      { notifyOnSubscribe: false },
    );
  }
}
